/**
 * The twelve pages that are not the home page.
 *
 * Content, but content that makes **checkable claims** — the SOC 2 status, "we never see a
 * private key", the comparison table, the changelog. Every word comes from `content.ts` so a
 * change to any of them is a reviewable diff rather than a string buried in markup.
 */

import { DOCS, TOOLS } from "@authoxi/ui-shared";
import { Ask } from "../components/Ask";
import { CHANGELOG, COMPARISON, FAQ, PILLARS, SECURITY, TERMS, VERDICT_CARDS } from "../content";
import { GetKey } from "./GetKey";

const FREE_TOOLS = [
  ["Agent Trust Grader", "Paste any agent or MCP endpoint and get a graded scan of whether it can be identified at all.", `${TOOLS}/grader`],
  ["Agent-Verified badge", "An embeddable SVG and snippet from a grade.", `${TOOLS}/badge`],
  ["Leaderboard", "Public rankings built from those scans.", `${TOOLS}/leaderboard`],
  ["Directory & whois", "A page per agent and operator, and a lookup that resolves a did to whoever published it.", `${TOOLS}/directory`],
  ["State of Agent Traffic", "A public report, as JSON and as a page.", `${TOOLS}/report`],
  ["Free observability", "Ship signed records with no account and read your own trail back — the key is the account.", `${TOOLS}/observe`],
  ["Verifiable receipt", "A named human approved this agent to do X. The whole signed event rides in the URL fragment, so we never learn you opened it.", `${TOOLS}/receipt`],
  ["Sign in with your Agent", "For relying parties. An agent presents a signed assertion, and you verify it against its own did:key — without ever talking to us.", `${TOOLS}/signin`],
  ["Standalone verifier", "npx -p @authoxi/js authoxi-verify — zero dependencies, no account, no network.", `${TOOLS}/verify`],
  ["Open specs", "Signed formats with cross-language vectors that fail CI if any implementation drifts.", `${TOOLS}/spec`],
] as const;

export function Why() {
  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">The gap</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s4)" }}>
            Four categories touch this. None of them owns it.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Not a competitive slight — a structural fact. Each was built for a different actor,
            and an agent is not that actor.
          </p>
          <div className="scroll-x">
            <table className="gap-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>What it governs</th>
                  <th>Enforcement</th>
                  <th>Human approval</th>
                  <th>Audit</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((r) => (
                  <tr className={r.us ? "us" : ""} key={r.cat}>
                    <td className="cat">
                      {r.cat}
                      <span className="ex">{r.ex}</span>
                    </td>
                    <td>{r.governs}</td>
                    <td>{r.enforces}</td>
                    <td>{r.approval}</td>
                    <td>{r.audit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card pad-lg" style={{ marginTop: "var(--s7)", maxWidth: "var(--measure)" }}>
            <p className="label">Never a key vault</p>
            <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)" }}>
              Agents never hold provider credentials. The enforcement point does — inside your own
              boundary if you self-host, or sealed under your KMS if you don't. A vault hands the
              key over and hopes; this never hands it over.
            </p>
          </div>
        </div>
      </section>
      <Ask id="ask-why" source="why" />
    </>
  );
}

export function How() {
  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">How it works</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "22ch", marginBottom: "var(--s4)" }}>
            Four outcomes, not two.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Every call returns one of four. The fourth is the one most systems lack:{" "}
            <em>not yet, and here is what would change that.</em>
          </p>
          <div className="pillars">
            {VERDICT_CARDS.map(([v, title, body]) => (
              <article className="pillar" key={v}>
                <span className={`v dim v-${v}`}>{v}</span>
                <h3 style={{ fontSize: "1.05rem" }}>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>

          <div className="rule" style={{ marginTop: "var(--s9)" }}>
            <span className="label">The four pillars</span>
          </div>
          <div className="pillars">
            {PILLARS.map((p) => (
              <article className="pillar" key={p.n}>
                <span className="n">
                  {p.n} · {p.governs}
                </span>
                <h3>{p.name}</h3>
                <p>{p.body}</p>
              </article>
            ))}
          </div>

          <div className="rule" style={{ marginTop: "var(--s9)" }}>
            <span className="label">Evidence</span>
          </div>
          <div className="qs">
            <div>
              <h2 className="xl" style={{ fontSize: "1.75rem", marginBottom: "var(--s4)" }}>
                It decides. It does not retain.
              </h2>
              <p className="lede">
                Every decision emits a signed <span className="mono">loss-event/v2</span> to an
                evidence store and is forgotten here. An auth service that also keeps the history
                has grown an audit product inside it, and the two <em>will</em> drift.
              </p>
              <p className="lede" style={{ marginTop: "var(--s4)" }}>
                The pump is never on the authorization path: bounded queue → disk spool → retry. A
                slow sink never slows a decision.
              </p>
            </div>
            <pre className="code">{`{
  "schema_version": "loss-event/v2",
  "decision": "allow",
  "agent_did": "did:key:z6Mk…4Rn",
  "amount": "9000.00",
  "approved_by": {
    "did": "did:key:z6Mk…9Qa",
    "email": "ops@acme.com",
    "signature": "ed25519:…"
  },
  "witness": "self"
}
// canonical JSON · sorted keys · floats refused
// three implementations, one vector, checked in CI`}</pre>
          </div>
        </div>
      </section>
      <Ask id="ask-how" source="how" />
    </>
  );
}

export function Security() {
  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">Security</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s4)" }}>
            The things that would be embarrassing to get wrong.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Stated as properties, because a security page that lists certifications instead of
            properties is telling you which audit it passed, not what it does.
          </p>
          <div className="stack">
            {SECURITY.map(([t, b]) => (
              <div className="rung reveal" key={t}>
                <span
                  className="v v-allow"
                  style={{ width: 30, height: 30, justifyContent: "center", padding: 0 }}
                >
                  ✓
                </span>
                <div>
                  <strong>{t}</strong>
                  <p>{b}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Answered rather than confessed. "No SOC 2 yet" is true and it ends the
              conversation; the same fact with its trajectory attached answers the procurement
              question instead, which is the only reason anyone asked. */}
          <div className="card pad-lg" style={{ marginTop: "var(--s7)" }}>
            <p className="label">Certification status</p>
            <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
              <strong>SOC 2 Type I: in progress.</strong> Not yet attested, and we will not imply
              otherwise — it is the first thing a buyer verifies. Until it lands, what you can
              check yourself is on this page: every property above is a property, not a
              certificate, and each one has a test behind it.
            </p>
            <p style={{ margin: "var(--s4) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
              Compliance packs state what they do <strong>not</strong> cover, in the pack itself. A
              record signed by the agent that produced it says <span className="mono">witness: self</span>,
              because self-attested and independently countersigned are different things and a
              format that let them look alike would be doing its readers real harm.
            </p>
          </div>
        </div>
      </section>
      <Ask id="ask-security" source="security" />
    </>
  );
}

export function Pricing() {
  const tiers = [
    ["Free · forever", "Open", "Everything you can do without an account.", ["Signed records, self-verified", "Trust grader + badge", "Directory, whois, specs", "Offline verifier CLI", "Self-serve key"], `${TOOLS}/`, "Use it now", false],
    ["Design partner", "Governed", "The gate, the humans, the evidence.", ["Everything in Open", "Mandates, budgets, keyring", "Human approval, signed", "Shadow mode + proposals", "MCP firewall", "Evidence retained & searchable"], "/partners", "Become a design partner", true],
    ["Self-hosted", "Your boundary", "For when the data plane cannot leave.", ["Everything in Governed", "Runs in your VPC", "Your KMS holds the keys", "We never see the traffic", "Evidence stays yours"], "/partners", "Talk to us", false],
  ] as const;

  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">Pricing</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "22ch", marginBottom: "var(--s4)" }}>
            The blocks are free. The evidence is what you buy.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Charging for enforcement means charging people to be safe, and they will turn it off.
            So enforcement is free forever and the audit trail is the product.
          </p>
          <div className="pillars cols-3">
            {tiers.map(([eyebrow, name, blurb, features, href, cta, feature]) => (
              <article
                className="pillar"
                key={name}
                style={feature ? { borderColor: "var(--action-text)" } : undefined}
              >
                <span className="n">{eyebrow}</span>
                <h3>{name}</h3>
                <p style={{ marginBottom: "var(--s4)" }}>{blurb}</p>
                {features.map((f) => (
                  <div className="sm" style={{ color: "var(--ink-2)", padding: "3px 0" }} key={f}>
                    · {f}
                  </div>
                ))}
                <a
                  className={`btn ${feature ? "btn-go" : ""}`}
                  style={{ marginTop: "var(--s5)", width: "100%" }}
                  href={href}
                >
                  {cta}
                </a>
              </article>
            ))}
          </div>
          <p className="sm muted" style={{ marginTop: "var(--s6)" }}>
            No published per-seat price during private beta. Quoting a number we have not tested
            against a real deployment would be a number we would have to retract.
          </p>
        </div>
      </section>
      <Ask id="ask-pricing" source="pricing" />
    </>
  );
}

export function Partners() {
  const cards = [
    ["what you get", "Everything, free, for the term", "The gate, mandates, budgets, the keyring, signed human approval, shadow mode and the MCP firewall. Self-hosted inside your own boundary if the data plane cannot leave."],
    ["what you get", "A price in writing, at the end", "Fixed before you decide, not at renewal. There is no published per-seat number because we have not tested one against a real deployment — quoting it now would be a number we would have to retract."],
    ["what we ask", "An hour a fortnight", "With whoever actually runs the fleet. Not a steering committee, and not somebody who owns the budget but has never read a denial reason."],
    ["what we ask", "Tell us where it is wrong", "Especially about challenge. It is the outcome most systems lack and the one easiest to model badly — if it is the wrong shape for your fleet, that is the single most useful thing you can tell us."],
  ] as const;

  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">Design partners · ten places</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s4)" }}>
            Bring one agent. The one that scares you.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Not a pilot of the whole fleet — one agent, on the path where a wrong answer costs
            something. Ten places, because ten is the number we can support properly while still
            writing code. That is a capacity limit, not a growth tactic.
          </p>
          <div className="pillars cols-2">
            {cards.map(([eyebrow, h, b]) => (
              <article className="pillar" key={h}>
                <span className="n">{eyebrow}</span>
                <h3>{h}</h3>
                <p>{b}</p>
              </article>
            ))}
          </div>
          <div className="card pad-lg" style={{ marginTop: "var(--s7)", maxWidth: "var(--measure)" }}>
            <p className="label">What is not part of the deal</p>
            <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)" }}>
              Your logo. A reference is something you may offer later if this earns it, and your
              name appears nowhere until you have said in writing that it may — which is also why
              this site carries no logos it has not been given. A buyer checks the first one they
              recognise, and one borrowed name costs more than every name it was meant to buy.
            </p>
          </div>
        </div>
      </section>
      <Ask id="ask-partners" source="partners" />
    </>
  );
}

export { GetKey };

export function Free() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Free · no account</span>
        </div>
        <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s4)" }}>
          Tools that are useful before you are a customer.
        </h1>
        <p className="lede" style={{ marginBottom: "var(--s7)" }}>
          Nothing here is gated, and none of it asks for an email before it has done something for
          you.
        </p>
        <div className="pillars cols-3">
          {FREE_TOOLS.map(([t, b, href]) => (
            <a className="pillar" href={href} key={t} style={{ textDecoration: "none" }}>
              <h3 style={{ fontSize: "1.1rem", marginTop: 0 }}>{t}</h3>
              <p>{b}</p>
            </a>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: "var(--s7)" }}>
          <a className="btn btn-go btn-lg" href={`${TOOLS}/`}>
            Open the free tools →
          </a>
        </div>
      </div>
    </section>
  );
}

export function Changelog() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Changelog</span>
        </div>
        <h1 className="xl" style={{ maxWidth: "20ch", marginBottom: "var(--s4)" }}>
          What changed, and when.
        </h1>
        <p className="lede" style={{ marginBottom: "var(--s7)" }}>
          Specific, because “various improvements” tells a reader only that somebody was obliged
          to write something.
        </p>
        {CHANGELOG.map(([date, items]) => (
          <div className="log-entry" key={date}>
            <p className="log-date">{date}</p>
            <ul className="log-list">
              {items.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <>
      <section className="section">
        <div className="wide">
          <div className="rule">
            <span className="label">Questions</span>
          </div>
          <h1 className="xl" style={{ maxWidth: "22ch", marginBottom: "var(--s4)" }}>
            The things people actually ask.
          </h1>
          <p className="lede" style={{ marginBottom: "var(--s7)" }}>
            Including the awkward ones, which are the only ones worth a page.
          </p>
          {/* <details>, not JS: keyboard-accessible, findable by the browser's own find, and
              open by default if scripting is off. */}
          <div className="faq">
            {FAQ.map(([q, a]) => (
              <details className="faq-item" key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <Ask id="ask-faq" source="faq" />
    </>
  );
}

export function Privacy() {
  const cards = [
    ["what is stored", "Four fields", "Your email, if you typed it into a field. Which surface it came from. What you were looking at — the thing we would be writing to you about. A timestamp. Nothing on the free tools is gated, so you can use all of them without giving us any of it."],
    ["what is not", "No enrichment, no selling, no advertising", "We do not look your address up, buy data about you, or attach a company profile to it. It is never sold, at any price, including in an acquisition. There is no advertising pixel and no ad network on any page here."],
    // THIS CARD IS NEW, AND IT REPLACED A SENTENCE THAT SAID THERE WAS NO ANALYTICS SCRIPT.
    // There is one now. Rewriting the claim rather than quietly leaving it is the entire point:
    // a governance product caught with a stale privacy page has lost the only argument it sells.
    ["measurement", "Counted, not identified", "These pages do count views, because we cannot tell what is worth writing next without knowing what gets read. It is cookieless: no cookie, no stored identifier, nothing that survives the tab, and no session recording. Two consequences we accept — we cannot tell a returning reader from a new one, and Do Not Track is honoured, so if your browser sends it we count nothing at all. The operator console is a different bargain and says so on the next line."],
    ["inside the console", "Your tenant, not your people", "Behind the key, usage is tied to a TENANT id — the account we already bill and support — so we can see which screens get used and which get abandoned. Never to a person: no operator name, no email, and no session replay. Sign out and the association is dropped."],
    ["your agents", "We never see a private key", "Agents generate their own. Proof-of-possession means a breach of us cannot impersonate your fleet — and it also means there is nothing of yours here to leak."],
    ["the evidence", "It decides, it does not retain", "Every decision emits a signed record to an evidence store and is forgotten here. Self-host and the traffic never reaches us at all."],
  ] as const;

  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Privacy</span>
        </div>
        <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s4)" }}>
          An address, and which tool you were using.
        </h1>
        <p className="lede" style={{ marginBottom: "var(--s7)" }}>
          That is the whole collection. Nothing inferred, nothing enriched, no identity graph — the
          moment that table knows more about you than you told us, it has become something you did
          not agree to.
        </p>
        <div className="pillars cols-2">
          {cards.map(([eyebrow, h, b]) => (
            <article className="pillar" key={h}>
              <span className="n">{eyebrow}</span>
              <h3>{h}</h3>
              <p>{b}</p>
            </article>
          ))}
        </div>
        <div className="card pad-lg" style={{ marginTop: "var(--s7)", maxWidth: "var(--measure)" }}>
          <p className="label">Getting rid of it</p>
          <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)" }}>
            One click, no login, immediate — there is no processing window and no preference centre
            to argue with. Every email we send carries the link. Access, correction and deletion
            requests go to <span className="mono">privacy@authoxi.com</span> and are answered by a
            person.
          </p>
        </div>
      </div>
    </section>
  );
}

export function Terms() {
  return (
    <section className="section">
      <div className="wide">
        <div className="rule">
          <span className="label">Terms</span>
        </div>
        <h1 className="xl" style={{ maxWidth: "24ch", marginBottom: "var(--s7)" }}>
          What this is, and what it is not.
        </h1>
        <div className="stack">
          {TERMS.map(([t, b]) => (
            <div className="rung" key={t}>
              <span
                className="v v-allow"
                style={{ width: 30, height: 30, justifyContent: "center", padding: 0 }}
              >
                §
              </span>
              <div>
                <strong>{t}</strong>
                <p>{b}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Docs() {
  return (
    <section className="section">
      <div className="page">
        <div className="card pad-lg" style={{ textAlign: "center" }}>
          <h1 className="xl" style={{ fontSize: "1.75rem", margin: "var(--s4) 0 var(--s3)" }}>
            Documentation
          </h1>
          <p className="lede" style={{ margin: "0 auto" }}>
            The spec, the SDK reference and a verifier you can run in the page live on the docs
            site. Nothing here pretends to be it.
          </p>
          <a className="btn btn-go" style={{ marginTop: "var(--s5)" }} href={DOCS} rel="external">
            Open the docs
          </a>
        </div>
      </div>
    </section>
  );
}
